/**
 * CROSS-CUTTING constants only. Shared file — changes need lead sign-off.
 *
 * Subsystem-specific numbers live in `src/<subsystem>/tuning.js`, owned by that subsystem,
 * so parallel agents never contend on this file.
 */

export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;

/** Never simulate more than this many fixed steps in one rendered frame (anti death-spiral). */
export const MAX_STEPS_PER_FRAME = 5;

/** Clamp on wall-clock delta fed to the accumulator, seconds. */
export const MAX_FRAME_DELTA = 0.25;

export const CONFIG = Object.freeze({
  SIM_HZ,
  SIM_DT,
  MAX_STEPS_PER_FRAME,
  MAX_FRAME_DELTA,

  /** Reference resolution the art direction and HUD layout are authored against. */
  REF_WIDTH: 1920,
  REF_HEIGHT: 1080,

  /** Units are metres, seconds, radians throughout. */
  GRAVITY: 9.81,

  round: Object.freeze({
    startSeconds: 60,
    /**
     * Hard ceiling so a good run cannot become infinite.
     *
     * ROUND 12: 150 -> 240, at the lead's direction after the game-feel adjudication. An
     * eight-repair chain pegged the timer at exactly 150.0 every single time, so the reward
     * for chaining stopped existing precisely when the player was playing best — the one
     * moment the economy is supposed to feel loudest. `gameplay` also now converts anything
     * over the ceiling into score (TUNING.payout.overflowScorePerSecond) so a capped chain
     * still pays, rather than silently discarding it.
     */
    maxSeconds: 240,
    countdownSeconds: 3,
    /** Warning beeps fire at these remaining-second marks. */
    warnAt: [30, 20, 10, 5, 4, 3, 2, 1],
  }),

  /** Weather ids. `gameplay` owns which is active; `look` owns what it looks like. */
  weathers: Object.freeze(['thaw', 'winter', 'nightrain']),

  /** Shared quality tiers. `look` reads this; everyone else may read it to scale counts. */
  quality: Object.freeze({
    level: 'high',            // 'low' | 'medium' | 'high' — set by core from probe or ?quality=
    shadows: true,
    post: true,
    particleScale: 1.0,
    trafficScale: 1.0,
  }),

  debug: Object.freeze({
    /** Enabled by ?debug=1 */
    enabled: false,
  }),
});

/** Phases of the round. */
export const PHASE = Object.freeze({
  BOOT: 'boot',
  TITLE: 'title',
  COUNTDOWN: 'countdown',
  PLAYING: 'playing',
  TIMEUP: 'timeup',
  RESULT: 'result',
});
